import { MessageExtendComplex } from '../MessageExtend/MessageExtendComplex';
import { BaseMsgProps } from './types';
import QuoteMessage from '../QuoteMessage';
import { useChatContext } from '../../contexts/ChatContext';
import { memo, useEffect, useRef, useState } from 'react';
import { getFacebookMessage } from 'services/api/conversation';
import { noop } from 'lodash-es';
import { FileOutlined, LoadingOutlined } from '@ant-design/icons';
import { formatNumber } from 'utils/numberFormat';
import { useInViewport } from 'ahooks';
import download from 'utils/download';
import styled from '@emotion/styled';
import { useUpdateMessage } from 'hooks/business/message';

export const FileMsg = memo((props: BaseMsgProps) => {
  const { raw, msg, conversation } = props;

  const { topBarInfo } = conversation;

  const {
    msgId,
    storeId,
    localStatus,
    fbMsgContent: {
      expireTimestamp,
      attachments: { data: [{ fileUrl, name, size } = {}] = [] } = {},
    } = {},
  } = msg.raw;

  const updateMessage = useUpdateMessage();

  const { dialogRef, clickQuote } = useChatContext();

  const ref = useRef(null);

  const [inViewPort] = useInViewport(ref, {
    root: dialogRef?.dom,
  });

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (inViewPort) {
      setLoaded(true);
    }

    if (
      !inViewPort ||
      loaded ||
      !Number(expireTimestamp) ||
      Number(expireTimestamp) <= 0 ||
      Date.now() > Number(expireTimestamp)
    ) {
      return;
    }

    getFacebookMessage({ storeId: storeId, msgId: msgId })
      .then(({ fbMsgContent }) => {
        updateMessage(msgId, (prev) => {
          if (!prev) {
            return;
          }
          return {
            ...prev,
            fbMsgContent,
          };
        });
      })
      .catch(noop);
  }, [inViewPort, loaded, expireTimestamp, msgId, storeId, updateMessage]);

  return (
    <MessageExtendComplex
      {...props}
      content={
        <QuoteMessage
          message={raw}
          storeName={topBarInfo?.storeName}
          wrap
          onQuoteClick={clickQuote}
        >
          <Container ref={ref} onClick={() => fileUrl && download(fileUrl, name)}>
            <FileOutlined />
            <section>
              <p>{name}</p>
              {size ? <div>{formatNumber(size, '0,0')}KB</div> : null}
            </section>
            {localStatus === 'uploading' && (
              <div>
                <LoadingOutlined />
              </div>
            )}
          </Container>
        </QuoteMessage>
      }
    />
  );
});

const Container = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  max-width: 320px;
  background: var(--fett-bg-panel);
  padding: 14px;
  border-radius: 4px;
  > span {
    flex: 0 0 auto;
    margin-right: 14px;
    font-size: 32px;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    overflow: hidden;
    > p {
      margin-bottom: 0;
      word-break: break-all;
    }
    > div {
      color: var(--fett-text-tertiary);
    }
  }
  > div {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.3);
    color: #fff;
  }
`;
